// src/components/ui/LenderShell.jsx
import React from "react";
import { Outlet } from "react-router-dom";
import Card from "./Card.jsx";
import LenderSideNav from "./LenderSideNav.jsx";
import NotificationCenter from "./NotificationCenter.jsx"; // new, default import
import { usePortalSessionGuard } from "../../hooks/usePortalSessionGuard.js";

export default function LenderShell() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  usePortalSessionGuard("lender");

  // Full page load so "/" is served by the Next.js marketing site, not this app's Home route.
  const goToPlatformHome = () => window.location.assign("/");

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      {/* Top Header */}
      <Card className="p-4 md:p-6 mb-6 flex items-center justify-between sticky top-4 z-40 bg-white/95 backdrop-blur-sm border-slate-200/80">
        <div className="flex items-center gap-3">
          {/* Hamburger (Mobile Only) */}
          <button
            type="button"
            className="md:hidden p-2 -ml-2 rounded-lg text-slate-600 hover:bg-slate-100"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          <button
            type="button"
            onClick={goToPlatformHome}
            aria-label="Go to Platform Homepage"
            title="Go to Platform Homepage"
            className="flex items-center gap-3 rounded-xl hover:bg-sky-50/80 p-1 pr-2 transition focus:outline-none focus:ring-2 focus:ring-sky-400"
          >
            <img
              src="/dynesourcer-logo.svg"
              alt="DyneSourcer"
              className="h-12 md:h-14 w-auto object-contain flex-shrink-0"
            />
            <div className="hidden sm:block text-left">
              <h1 className="text-lg md:text-xl font-extrabold text-slate-900">lenderPortal</h1>
              <p className="text-xs text-gray-500">Manage your products, analytics, and more.</p>
            </div>
          </button>

          <div className="sm:hidden">
            <h1 className="text-xl font-extrabold text-slate-900">lenderPortal</h1>
          </div>
        </div>

        {/* Right side: Notification Center only */}
        <div className="flex items-center gap-3">
          <NotificationCenter />
        </div>
      </Card>

      {/* Main Layout */}
      <div className="grid md:grid-cols-[260px_1fr] gap-6 min-h-[calc(100vh-8rem)] relative">
        {/* Desktop Sidebar (hidden on mobile) */}
        <div className="hidden md:block h-full sticky top-28 self-start">
          <LenderSideNav />
        </div>

        {/* Mobile Sidebar (Drawer) */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
              onClick={() => setMobileMenuOpen(false)}
            />
            {/* Drawer */}
            <div className="absolute left-0 top-0 bottom-0 w-3/4 max-w-xs bg-white shadow-2xl p-4 transition-transform duration-300">
              <div className="flex items-end justify-end mb-2">
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 bg-gray-100 rounded-full"
                  aria-label="Close menu"
                >
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <LenderSideNav onClose={() => setMobileMenuOpen(false)} />
            </div>
          </div>
        )}

        <div className="min-w-0 max-w-full space-y-6 min-h-full overflow-x-hidden">
          {/* Nested lender pages render here */}
          <Outlet />
        </div>
      </div>
    </div>
  );
}
